/* Bewertungen aus der Herlamora-App in die Review-Section spielen.

   Zwei Endpunkte des App-Proxys:
   - /ratings  Durchschnitte je Kriterium (ersetzen die Theme-Werte)
   - /reviews  Einzelbewertungen, nur freigegebene, fuer das Fenster

   Die im Theme gepflegten Werte bleiben der Rueckfall: Sie stehen im
   Markup und werden nur ueberschrieben, wenn die App tatsaechlich
   Bewertungen fuer die Kategorie liefert. Faellt der Proxy aus, sieht
   die Seite deshalb aus wie vorher. */
(() => {
  const BASE = '/apps/herlamora-horses';
  const PAGE = 10;

  /* Unterscheidet "App sagt: noch keine Bewertungen" von "App antwortet nicht". */
  const NO_REVIEWS = Symbol('no-reviews');

  const num = (value) => Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  /* Die App liefert 1..5, der Regler erwartet 0..100. */
  const toPercent = (value) => Math.max(0, Math.min(100, ((value - 1) / 4) * 100));

  const query = (root, extra = {}) => {
    const params = new URLSearchParams({ category: root.dataset.category, ...extra });
    if (root.dataset.variant) params.set('variant', root.dataset.variant);
    return params.toString();
  };

  async function loadRatings(root) {
    const response = await fetch(`${BASE}/ratings?${query(root)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const payload = await response.json();
    if (!payload?.ok || !Array.isArray(payload.ratings)) return null;
    /* Ohne Variante kann die App mehrere Zeilen liefern - die erste mit
       Bewertungen ist die gesuchte. */
    const entry = payload.ratings.find((item) => item.count > 0);
    if (entry) return entry;
    /* Die App hat geantwortet und kennt die Kategorie (sonst waere es ein
       404 gewesen), es gibt nur noch keine freigegebene Bewertung. Das ist
       etwas anderes als "keine Antwort" und bekommt deshalb den
       Leerzustand statt der Theme-Rueckfallwerte. */
    return NO_REVIEWS;
  }

  function applyRatings(root, data) {
    const rating = root.querySelector('[data-review-rating]');
    if (rating && typeof data.rating === 'number') rating.textContent = num(data.rating);

    const count = root.querySelector('[data-review-count]');
    if (count && data.count) {
      count.textContent = data.count === 1 ? '1 Bewertung' : `${data.count} Bewertungen`;
    }

    /* Regler ueber den Kriteriennamen zuordnen. Die Beschriftungen links,
       mittig und rechts bleiben aus dem Theme - die App liefert nur das
       Label des aktuellen Werts, nicht die Endpunkte der Skala. */
    const scales = new Map();
    root.querySelectorAll('[data-scale]').forEach((el) => {
      scales.set(el.dataset.scale.trim().toLowerCase(), el);
    });

    (data.criteria || []).forEach((criterion) => {
      const el = scales.get(String(criterion.name).trim().toLowerCase());
      const marker = el?.querySelector('[data-scale-marker]');
      if (!marker) return;
      marker.style.left = `${toPercent(criterion.value)}%`;
      const track = el.querySelector('.hrs__track');
      if (track) {
        track.setAttribute('aria-label', `${criterion.name}: ${criterion.label}`);
      }
    });
  }

  function reviewItem(review) {
    const li = document.createElement('li');
    li.className = 'hrs-modal__item';

    const head = document.createElement('div');
    head.className = 'hrs-modal__item-head';
    head.innerHTML = `<span class="hrs-modal__item-author"></span><span class="hrs-modal__item-date"></span>`;
    head.querySelector('.hrs-modal__item-author').textContent = review.author;
    head.querySelector('.hrs-modal__item-date').textContent =
      new Date(review.createdAt).toLocaleDateString('de-DE');
    li.append(head);

    const stars = document.createElement('p');
    stars.className = 'hrs-modal__item-rating';
    stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    stars.setAttribute('aria-label', `${review.rating} von 5 Sternen`);
    li.append(stars);

    if (review.comment) {
      const text = document.createElement('p');
      text.className = 'hrs-modal__item-text';
      text.textContent = review.comment;
      li.append(text);
    }

    if (review.criteria?.length) {
      const ul = document.createElement('ul');
      ul.className = 'hrs-modal__item-criteria';
      review.criteria.forEach((criterion) => {
        const item = document.createElement('li');
        item.textContent = `${criterion.name}: ${criterion.label}`;
        ul.append(item);
      });
      li.append(ul);
    }

    return li;
  }

  async function loadReviews(root, dialog, reset) {
    const list = dialog.querySelector('[data-reviews-list]');
    const status = dialog.querySelector('[data-reviews-status]');
    const more = dialog.querySelector('[data-reviews-more]');

    if (reset) {
      list.replaceChildren();
      dialog.dataset.offset = '0';
    }

    const offset = Number(dialog.dataset.offset || 0);
    status.hidden = false;
    status.textContent = 'Bewertungen werden geladen …';
    more.hidden = true;

    try {
      const response = await fetch(
        `${BASE}/reviews?${query(root, { limit: String(PAGE), offset: String(offset) })}`,
        { headers: { Accept: 'application/json' } },
      );
      const payload = response.ok ? await response.json() : null;

      if (!payload?.ok) throw new Error(payload?.error || 'Bewertungen nicht verfügbar.');

      payload.reviews.forEach((review) => list.append(reviewItem(review)));
      dialog.dataset.offset = String(offset + payload.reviews.length);

      if (!list.children.length) {
        status.textContent = 'Für dieses Produkt gibt es noch keine Bewertungen.';
      } else {
        status.hidden = true;
      }
      more.hidden = !payload.hasMore;
    } catch (error) {
      status.hidden = false;
      status.textContent = error.message || 'Bewertungen konnten nicht geladen werden.';
    }
  }

  function init(root) {
    if (!root.dataset.category || root.dataset.bound) return;
    root.dataset.bound = 'true';

    const dialog = document.getElementById(
      root.querySelector('[data-reviews-open]')?.getAttribute('data-reviews-open') || '',
    );
    const openButton = root.querySelector('[data-reviews-open]');

    /* Ohne Antwort der App bleiben die im Theme konfigurierten Werte stehen
       und der Button bleibt verborgen -- das Modal haette nichts zu zeigen. */
    loadRatings(root)
      .then((data) => {
        if (!data) return;
        if (data === NO_REVIEWS) {
          root.dataset.reviews = 'empty';
          return;
        }
        root.dataset.reviews = 'ok';
        applyRatings(root, data);
        if (openButton) openButton.hidden = false;
      })
      .catch(() => {});

    if (!dialog || !openButton) return;

    openButton.addEventListener('click', () => {
      dialog.showModal();
      if (!dialog.dataset.loaded) {
        dialog.dataset.loaded = 'true';
        loadReviews(root, dialog, true);
      }
    });

    dialog.querySelector('[data-reviews-close]')?.addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-reviews-more]')?.addEventListener('click', () =>
      loadReviews(root, dialog, false),
    );
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  const boot = () => document.querySelectorAll('[data-review-summary]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
